import { format } from '@breezeway/brzw-react-core';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Field } from 'formik';
import moment from 'moment';
import _ from 'lodash';
import { UncontrolledTooltip } from 'reactstrap';

import TASK_CONST from '@shared/task/constants';
import SelectFormik from '../../styleguide/select_formik';
import BrzwDayPicker from '../../../../brzw_day_picker';
import BrzwTimePicker from '../../../../brzw_time_picker';
import {
  DATE_FORMAT,
  DAY_FORMAT,
  DEFAULT_FORMATTED_HOUR,
  getRecurrenceTextByDate,
  TIME_FORMAT,
} from './helper';

import './date_time_picker_with_recurring_formik.scss';

const CLASS_NAME = 'date-time-picker-with-recurring-formik';
const SCHEDULE_TOOLTIP_ID = 'schedule-field-wrapper';

class DateTimePickerWithRecurringFormik extends Component {
  constructor(props) {
    super(props);

    this.getRecurrenceLabel = this.getRecurrenceLabel.bind(this);
    this.onDateRecurranceChange = this.onDateRecurranceChange.bind(this);
    this.onDayChange = this.onDayChange.bind(this);
    this.onTimeChange = this.onTimeChange.bind(this);
  }

  onTimeChange(pickedTime) {
    const { form, field } = this.props;

    if (!pickedTime || _.isNaN(pickedTime.getTime())) {
      form.setFieldValue(`${field.name}.start_time`, null);
    } else {
      let formattedTime = DEFAULT_FORMATTED_HOUR;
      if (pickedTime) {
        formattedTime = moment(pickedTime).format(TIME_FORMAT);
      }
      form.setFieldValue(`${field.name}.start_time`, formattedTime);
    }
  }

  onDayChange(pickedDate) {
    const { form, field } = this.props;

    if (!pickedDate) {
      form.setFieldValue(`${field.name}.start_date`, null);
      form.setFieldValue(`${field.name}.start_time`, null);
    } else {
      const pickedDateFormatted = moment(pickedDate);
      const formattedDate = pickedDateFormatted.format(DATE_FORMAT);
      const isRepeating = _.get(field.value, 'checkbox_recurring');
      const endDate = _.get(field.value, 'end_date');
      const startTime = _.get(field.value, 'start_time');
      const currentMomentDate = moment();
      const isTodaySelected = currentMomentDate.isSame(pickedDateFormatted, 'day');
      const isTimeInvalid = isTodaySelected && startTime < moment().format(TIME_FORMAT);

      form.setFieldValue(`${field.name}.start_date`, formattedDate);

      if (isTimeInvalid) {
        form.setFieldValue(`${field.name}.start_time`, null);
      }

      const shouldUpdateEndDate = endDate && isRepeating && moment(endDate) < moment(pickedDate);
      if (shouldUpdateEndDate) {
        form.setFieldValue(`${field.name}.end_date`, formattedDate);
      }
    }
  }

  onDateRecurranceChange(pickedDate) {
    const { field, form } = this.props;

    if (!pickedDate) {
      form.setFieldValue(`${field.name}.end_date`, null);
    } else {
      const formattedDate = moment(pickedDate).format(DATE_FORMAT);
      form.setFieldValue(`${field.name}.end_date`, formattedDate);
    }
  }

  getRecurrenceLabel(type) {
    const { field } = this.props;
    const startTimeValue = _.get(field.value, 'start_time');
    const startDateValue = _.get(field.value, 'start_date');

    return getRecurrenceTextByDate(
      type,
      format.datetime.formatTime(startTimeValue),
      startDateValue,
    );
  }

  getFrequencyOptions() {
    return _.map(TASK_CONST.FREQUENCY_OPTIONS, frequency => ({
      label: this.getRecurrenceLabel(frequency),
      value: frequency.value,
    }));
  }

  renderScheduleTooltip() {
    const { field, taskId } = this.props;
    const frequencyValue = _.get(field.value, 'frequency');
    const startDateValue = _.get(field.value, 'start_date');

    if (_.isEmpty(startDateValue)) return null;

    let tooltipText = `Due on ${moment(startDateValue, DATE_FORMAT).format('ddd MMM D, YYYY')}`;
    const tooltipId = `${SCHEDULE_TOOLTIP_ID}-${taskId}`;

    if (!_.isEmpty(frequencyValue)) {
      const frequencyName = _.find(this.getFrequencyOptions(), { value: frequencyValue });
      const frequencyTextArray = _.split(_.get(frequencyName, 'label'), /[()]/);

      const frequencyText = _.join(
        _.map(frequencyTextArray, (value, index) => {
          switch (index) {
            case 0:
              return _.lowerCase(value);
            case 1:
              return `on ${value}`;
            default:
              return value;
          }
        }),
        ' ',
      );
      tooltipText = `Due ${frequencyText}`;
    }

    return (
      <UncontrolledTooltip
        className={`${CLASS_NAME}__schedule-tooltip`}
        placement="top"
        target={tooltipId}
      >
        {tooltipText}
      </UncontrolledTooltip>
    );
  }

  render() {
    const {
      className,
      disabled,
      field,
      form,
      isClearable,
      label,
      shouldShowScheduleTooltip,
      startDateIcon,
      taskId,
      theme,
    } = this.props;
    const { errors, submitCount } = form;

    const isSubmitted = submitCount > 0;
    const showRecurring = _.get(field.value, 'checkbox_recurring');
    const startDateValue = _.get(field.value, 'start_date');
    const startTimeValue = _.get(field.value, 'start_time');
    const endDateValue = _.get(field.value, 'end_date');
    const startDate = moment(startDateValue);
    let formattedStartDate = null;
    let formattedStartTime = null;
    let formattedEndDate = null;

    if (startDateValue) formattedStartDate = startDate.toDate();

    if (startTimeValue) {
      const startTime = moment(startTimeValue, TIME_FORMAT);
      formattedStartTime = startTime.toDate();
    }
    if (endDateValue) {
      const endDate = moment(endDateValue);
      formattedEndDate = endDate.toDate();
    }

    const isTodaySelected = startDate.isSame(moment(), 'day');
    const minTime = isTodaySelected ? new Date() : null;
    const frequencyOptions = this.getFrequencyOptions();
    const startDateError = _.get(errors, 'schedule.start_date');
    const startTimeError = _.get(errors, 'schedule.start_time');
    const frequencyError = _.get(errors, 'schedule.frequency');
    const endDateError = _.get(errors, 'schedule.end_date');
    const tooltipId = `${SCHEDULE_TOOLTIP_ID}-${taskId}`;

    return (
      <div className={classnames(CLASS_NAME, className)}>
        <div className={`${CLASS_NAME}__row`}>
          {label && <div className={`${CLASS_NAME}__label`}>{label}</div>}

          <div className={`${CLASS_NAME}__fields-wrapper`}>
            <div className={`${CLASS_NAME}__field`} id={tooltipId}>
              <BrzwDayPicker
                dateFormat={DAY_FORMAT}
                disabled={disabled}
                error={isSubmitted ? startDateError : null}
                futureOnly
                icon={startDateIcon}
                name="schedule-start-date"
                onChange={this.onDayChange}
                selected={formattedStartDate}
                theme={theme}
                withClearAction={isClearable}
              />
              {shouldShowScheduleTooltip && this.renderScheduleTooltip()}
            </div>
            <div className={`${CLASS_NAME}__field`}>
              <BrzwTimePicker
                disabled={disabled}
                error={isSubmitted ? startTimeError : null}
                minTime={minTime}
                onChange={this.onTimeChange}
                placeholderText="Add time"
                selected={formattedStartTime}
                theme={theme}
                withClearAction={isClearable}
              />
            </div>
          </div>
        </div>

        {showRecurring && (
          <div className={`${CLASS_NAME}__row`}>
            <div className={`${CLASS_NAME}__label`}>Repeats</div>

            <div className={`${CLASS_NAME}__fields-wrapper`}>
              <div className={`${CLASS_NAME}__field`}>
                <Field
                  className={`${CLASS_NAME}__dropdown`}
                  component={SelectFormik}
                  disabled={disabled}
                  error={frequencyError && isSubmitted}
                  label="Repeating"
                  name={`${field.name}.frequency`}
                  options={frequencyOptions}
                  placeholder="Select frequency"
                  theme="dark"
                />
              </div>
              <div className={`${CLASS_NAME}__field`}>
                <BrzwDayPicker
                  dateFormat={DAY_FORMAT}
                  datePickerProps={{
                    popperPlacement: 'bottom-end',
                  }}
                  disabled={disabled}
                  error={isSubmitted ? endDateError : null}
                  futureOnly
                  icon={null}
                  minDate={formattedStartDate}
                  name="schedule-end-date"
                  onChange={this.onDateRecurranceChange}
                  placeholderText="End date *"
                  required
                  selected={formattedEndDate}
                  theme={theme}
                  withClearAction={isClearable}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

DateTimePickerWithRecurringFormik.propTypes = {
  className: PropTypes.string,
  disabled: PropTypes.bool,
  end_date: PropTypes.shape({
    input: PropTypes.shape({
      onChange: PropTypes.func,
      value: PropTypes.string,
    }),
  }),
  field: PropTypes.shape({}),
  form: PropTypes.shape({}),
  isClearable: PropTypes.bool,
  label: PropTypes.string,
  shouldShowScheduleTooltip: PropTypes.bool,
  start_date: PropTypes.shape({
    input: PropTypes.shape({
      value: PropTypes.string,
    }),
  }),
  startDateIcon: PropTypes.string,
  start_time: PropTypes.shape({
    input: PropTypes.shape({
      onChange: PropTypes.func,
      value: PropTypes.string,
    }),
  }),
  taskId: PropTypes.number,
  theme: PropTypes.string,
};

export default DateTimePickerWithRecurringFormik;
